/**
 * state/SystemState.ts — the single source of truth for application-wide state.
 *
 * Roamer Core™ owns one global snapshot: who the user is, where the current
 * base is, whether we're online, sync status, theme, language, and each
 * engine's status. Everything else reads from (and subscribes to) this store —
 * no parallel copies of "current base" scattered across the app.
 */

import type { RoamerLocation } from '@/services/RoamerLocationEngine';
import type { EngineStatus } from '../registry/EngineRegistry';

export type SyncStatus = 'idle' | 'syncing' | 'synced' | 'error';
export type ThemeName = 'dark' | 'light';

export interface SystemStateSnapshot {
  currentUser: { id: string; displayName: string } | null;
  currentBase: RoamerLocation | null;
  currentSessionId: string | null;
  online: boolean;
  syncStatus: SyncStatus;
  theme: ThemeName;
  language: string;
  engineStatus: Record<string, EngineStatus>;
  /** Epoch ms of the last state mutation. */
  updatedAt: number;
}

export type SystemStateListener = (snapshot: SystemStateSnapshot, changedKeys: (keyof SystemStateSnapshot)[]) => void;

const DEFAULT_STATE: SystemStateSnapshot = {
  currentUser: null,
  currentBase: null,
  currentSessionId: null,
  online: true,
  syncStatus: 'idle',
  theme: 'dark',
  language: 'en',
  engineStatus: {},
  updatedAt: 0,
};

export class SystemStateStore {
  private state: SystemStateSnapshot;
  private listeners = new Set<SystemStateListener>();

  constructor(initial: Partial<SystemStateSnapshot> = {}) {
    this.state = { ...DEFAULT_STATE, ...initial };
    if (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean') {
      this.state.online = navigator.onLine;
    }
  }

  /** Immutable snapshot — callers can never mutate core state directly. */
  getSnapshot(): SystemStateSnapshot {
    return structuredClone(this.state);
  }

  /** Merge a partial update; notifies subscribers with the changed keys. */
  update(patch: Partial<Omit<SystemStateSnapshot, 'updatedAt'>>, now: number): SystemStateSnapshot {
    const changedKeys = Object.keys(patch) as (keyof SystemStateSnapshot)[];
    if (changedKeys.length === 0) return this.getSnapshot();
    this.state = { ...this.state, ...patch, updatedAt: now };
    const snapshot = this.getSnapshot();
    this.listeners.forEach((l) => {
      try {
        l(snapshot, changedKeys);
      } catch {
        // one broken subscriber never breaks state propagation
      }
    });
    return snapshot;
  }

  setEngineStatus(engineId: string, status: EngineStatus, now: number): void {
    this.update({ engineStatus: { ...this.state.engineStatus, [engineId]: status } }, now);
  }

  subscribe(listener: SystemStateListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Wire browser online/offline signals into the store. Returns un-wire fn. */
  attachConnectivityListeners(nowFn: () => number): () => void {
    if (typeof window === 'undefined') return () => {};
    const onOnline = () => this.update({ online: true }, nowFn());
    const onOffline = () => this.update({ online: false, syncStatus: 'idle' }, nowFn());
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }
}
